import React, { useState, useEffect } from "react"
import { HiOutlineArrowNarrowRight } from "react-icons/hi"
import { connect, ConnectedProps } from "react-redux"
import { useHistory, Link } from "react-router-dom"
import { Button, Input, InputGroup, InputGroupText, Spinner } from "reactstrap"
import { MyIcon, MyUI } from "src/core/components"
import { HAuth, HApi } from "src/core/helpers"
import { get } from "src/core/helpers/utils"
import {
  setHomepageUrl,
  getHomepageUrl,
  cacheMenuAfterLogin,
} from "src/core/helpers/auth"
import { Path } from "src/modules/app_dnt/constants/path"
import { PATH } from "src/helpers/paths"
import {
  ErrorMessage,
  Form,
  SubmitSpan,
  Title,
  FooterForm,
} from "./Login.styles"
import { login, loginSuccessWithResponse } from "./Login.thunks"
import { useTranslation } from "react-i18next"
import Container from "./LoginContainer"
import LoginWithGoogle from "./GoogleLogin"

const mapStateToProps = state => ({
  loading: state.login.loading,
  isAuthenticated: state.app.isAuthenticated,
})

const mapDispatchToProps = {
  login,
  loginSuccessWithResponse,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

interface Props extends ConnectedProps<typeof connector> {}

const Login = (props: Props) => {
  const { login, loading, loginSuccessWithResponse, isAuthenticated } = props
  const { t } = useTranslation()
  const [show2FA, setShow2FA] = useState(
    HAuth.getInfo2FA()["fa2_needenable"] === true ||
      HAuth.getInfo2FA()["fa2_needverify"] === true
  )
  const history = useHistory()
  // Only redirect on mount (handles "already logged in" case).
  // Do NOT put this in render body — it fires on every re-render caused by
  // dispatch(fetchLoginSuccess), which would navigate before MenuInfo is fetched.
  useEffect(() => {
    if (isAuthenticated) {
      history.push(getHomepageUrl())
    }
    // eslint-disable-next-line
  }, [])
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [isRedirecting, setIsRedirecting] = useState(false)
  // const logoApp =
  //   "https://lh3.googleusercontent.com/d/1xeCHhwlaiijnfiURlef2d0m-OTVr68JR=w0?authuser=0"
  const handleUsername = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(event.target.value)
  }

  const handlePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
  }

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!loading) {
      const payload = { username, password }
      login(payload)
        .then(({ res }) => {
          if (res?.user_type && res.user_type.indexOf("ForgotPassword_") > -1) {
            let token = res.user_type.replace("ForgotPassword_", "")
            if (token) {
              return history.push(`${PATH.RESET}/${token}`)
            }
          } else if (
            res?.fa2_needenable === true ||
            res?.fa2_needverify === true
          ) {
            // 2FA required — trigger re-render to show 2FA UI
            setShow2FA(true)
            return
          } else {
            setIsRedirecting(true)
            sessionStorage.setItem("_menuFetchedByLogin", "1")
            HApi.apiGeneric({
              method: "POST",
              url: "Menu/MenuInfo",
              data: {},
            })
              .then(menuRes => {
                const menuList = menuRes?.Data?.MenuList || []
                cacheMenuAfterLogin(menuList)
                if (!menuList || menuList.length === 0) {
                  history.push(PATH.MY_PROFILE)
                  return
                }
                const homepageItem = menuList.find(
                  (v: any) => v.IsHomepage === true
                )
                const homepageUrl = homepageItem
                  ? homepageItem.UIUrl ||
                    Path[homepageItem.ScreenCode] ||
                    PATH.HOME
                  : PATH.HOME
                setHomepageUrl(homepageUrl)
                history.push(homepageUrl)
              })
              .catch(() => {
                history.push(PATH.MY_PROFILE)
              })
          }
        })
        .catch(err => {
          setError(get(err, "error.response.data.error_description"))
        })
    }
  }
  const onShowPassword = value => {
    setShowPassword(value)
    let _inputPassword = document.getElementById("password")
    if (_inputPassword) {
      if (value) {
        _inputPassword["type"] = "text"
      } else {
        _inputPassword["type"] = "password"
      }
    }
  }
  if (isRedirecting) {
    return (
      <Container>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "3rem 1rem",
            gap: "1rem",
          }}
        >
          <Spinner
            color="primary"
            style={{ width: "2.5rem", height: "2.5rem" }}
          />
          <p style={{ margin: 0, color: "#6f7782", fontSize: "14px" }}>
            {t("login.LoadingData", "Đang tải dữ liệu...")}
          </p>
        </div>
      </Container>
    )
  }

  return (
    <Container>
      {show2FA ? (
        <Form
          className="pd-top-2rem rounded-sm text-center"
          onSubmit={e => {
            e.preventDefault()
          }}
        >
          <MyUI
            type="setting2fa_selectmethod2fa"
            fnList={{
              dispatchLoginSuccess: res => {
                loginSuccessWithResponse(res)
                sessionStorage.setItem("_menuFetchedByLogin", "1")
                setIsRedirecting(true)
                HApi.apiGeneric({
                  method: "POST",
                  url: "Menu/MenuInfo",
                  data: {},
                })
                  .then(menuRes => {
                    const menuList = menuRes?.Data?.MenuList || []
                    cacheMenuAfterLogin(menuList)
                    if (!menuList || menuList.length === 0) {
                      history.push(PATH.MY_PROFILE)
                      return
                    }
                    const homepageItem = menuList.find(
                      (v: any) => v.IsHomepage === true
                    )
                    const homepageUrl = homepageItem
                      ? homepageItem.UIUrl ||
                        Path[homepageItem.ScreenCode] ||
                        PATH.HOME
                      : PATH.HOME
                    setHomepageUrl(homepageUrl)
                    history.push(homepageUrl)
                  })
                  .catch(() => {
                    history.push(PATH.MY_PROFILE)
                  })
              },
            }}
          />
        </Form>
      ) : (
        <Form className="pd-top-2rem rounded-sm text-center" onSubmit={submit}>
          <Title style={{ marginTop: "0px" }}>{t("login.Title")}</Title>
          <InputGroup style={{ marginBottom: "1rem" }}>
            <InputGroupText>
              <MyIcon data="type:HiOutlineMail" />
            </InputGroupText>
            <Input
              placeholder={t("login.Username")}
              autoComplete="on"
              value={username}
              autoFocus
              name="username"
              onChange={handleUsername}
            />
          </InputGroup>
          <InputGroup style={{ marginBottom: "1rem" }}>
            <InputGroupText>
              <MyIcon data="type:HiOutlineKey" />
            </InputGroupText>
            <Input
              type={showPassword === true ? "text" : "password"}
              placeholder={t("login.Password")}
              autoComplete="on"
              value={password}
              name="password"
              onChange={handlePassword}
            />
            <InputGroupText
              style={{ cursor: "pointer" }}
              onClick={() => {
                onShowPassword(!showPassword)
              }}
            >
              <MyIcon
                data={
                  showPassword ? "type:HiOutlineEye" : "type:HiOutlineEyeOff"
                }
              />
            </InputGroupText>
          </InputGroup>
          {error && (
            <ErrorMessage className="mb-3 text-danger text-xl-center">
              {error}
            </ErrorMessage>
          )}
          <Button
            type="submit"
            color="primary"
            style={{ width: "100%" }}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                {t("btn.login")}{" "}
                <SubmitSpan>
                  <HiOutlineArrowNarrowRight />
                </SubmitSpan>
              </>
            )}
          </Button>
          <LoginWithGoogle onRedirecting={() => setIsRedirecting(true)} />
          <FooterForm>
            <Link to={PATH.FORGOT}>{t("login.Forgot Password")}</Link>
            {/* <Link to={PATH.REGISTER}>{t("login.Create Account")}</Link> */}
          </FooterForm>
        </Form>
      )}
    </Container>
  )
}

export default connector(Login)
